import * as turf from '@turf/turf';
import * as d3 from 'd3';
import _ from 'lodash';
import * as ss from 'simple-statistics';
import { FilterTypeEnum } from '../../../../enuns/filterType.enum';

export const getShapeCentroid = f => {
    const point = turf.centroid(f);
    const lat = point.geometry.coordinates[1];
    const lng = point.geometry.coordinates[0];
    return L.latLng(lat, lng);
};

export const getLargestPart = f => {
    const parts = f.geometry.coordinates.map(coordinates => turf.polygon(coordinates));
    return _.maxBy(parts, part => turf.area(part));
};

export const getLargestPartLabelPoint = f => {
    if (_.get(f, 'geometry.type') !== 'MultiPolygon') {
        return getShapeCentroid(f);
    }

    try {
        const largest = getLargestPart(f);

        let point = turf.centerOfMass(largest);
        if (!turf.booleanPointInPolygon(point, largest)) {
            point = turf.pointOnFeature(largest);
        }

        return L.latLng(point.geometry.coordinates[1], point.geometry.coordinates[0]);
    } catch (err) {
        console.log(err);
        return getShapeCentroid(f);
    }
};

export const boundsToTurfPolygon = (bbox, paddingRatio = 1) => {
    const polygon = turf.bboxPolygon(bbox);
    if (paddingRatio && paddingRatio !== 1) {
        return turf.transformScale(polygon, paddingRatio);
    }
    return polygon;
};

export const getVisibleCentroid = (feature, viewportPolygon) => {
    try {
        let clipped;
        try {
            clipped = turf.intersect(feature, viewportPolygon);
        } catch (err) {
            const hull = turf.convex(feature);
            clipped = hull ? turf.intersect(hull, viewportPolygon) : null;
        }

        if (!clipped) {
            return null;
        }

        let point = turf.centerOfMass(clipped);
        if (!turf.booleanPointInPolygon(point, clipped)) {
            point = turf.pointOnFeature(clipped);
        }

        return point.geometry.coordinates;
    } catch (err) {
        console.log(err);
        return null;
    }
};

export const isValidNumber = value => {
    return !_.isNaN(_.toNumber(value));
};

const hashStringToIndex = (value, size) => {
    const str = `${value}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % size;
};

export const getObjectThemeColor = (theme, val, defaultColor) => {
    if (theme[val]) {
        return theme[val];
    }

    const normalized = _.deburr(_.toLower(_.trim(`${val}`)));
    const matchedKey = _.findKey(
        theme,
        (color, key) => key !== 'extra' && _.deburr(_.toLower(key)) === normalized
    );

    if (matchedKey) {
        return theme[matchedKey];
    }

    const extra = _.isArray(theme.extra) ? theme.extra : [];
    if (extra.length && !_.isNil(val) && `${val}`.length) {
        return extra[hashStringToIndex(val, extra.length)];
    }

    return defaultColor || 'rgb(204,204,204)';
};

export const getDomainRange = (domain, symbology) => {
    domain = _.sortedUniq(domain);

    if (_.size(domain) < 1) {
        return;
    }

    if (!symbology.theme) {
        symbology.theme = [];
    }

    const domainRange = {
        nomenal() {
            const range = val => {
                if (symbology.theme && !_.isArray(symbology.theme)) {
                    return getObjectThemeColor(symbology.theme, val, symbology.defaultColor);
                }

                return symbology.theme[_.indexOf(domain, val) % _.size(symbology.theme)];
            };
            range.domain = () => domain;
            return range;
        },
        log() {
            return d3
                .scaleLog()
                .domain(domain)
                .range(symbology.theme);
        },
        ordinal() {
            return d3
                .scaleOrdinal()
                .domain(domain)
                .range(symbology.theme);
        },
        quantize() {
            return d3
                .scaleQuantize()
                .domain(domain)
                .range(symbology.theme);
        },
        linear() {
            return d3
                .scaleLinear()
                .domain(domain)
                .range(symbology.theme);
        },
        jenks() {
            const breaks = _.size(domain) < _.size(symbology.theme) ? _.size(domain) : _.size(symbology.theme);
            let cluster;
            let theme = symbology.theme;
            if (symbology.dualband) {
                const nTheme = _.slice(theme, 0, Math.floor(_.size(theme) / 2));
                const pTheme = _.slice(theme, Math.floor(_.size(theme) / 2));

                const nValues = _.filter(domain, d => d < 0);
                const nBreaks = _.min([_.size(nTheme), _.size(nValues)]);
                const nDomain = nBreaks ? ss.ckmeans(nValues, nBreaks) : [];

                const pValues = _.filter(domain, d => d >= 0);
                const pBreaks = _.min([_.size(pTheme), _.size(pValues)]);
                const pDomain = pBreaks ? ss.ckmeans(pValues, pBreaks) : [];

                cluster = _(_.concat(nDomain, pDomain));
                theme = _.concat(_.take(nTheme, nBreaks), _.takeRight(pTheme, pBreaks));
            }

            if (!symbology.dualband || _.size(theme) < 2) {
                cluster = _(ss.ckmeans(domain, breaks));
                theme = symbology.theme;
            }

            let range = cluster
                .map(c => c[0])
                .orderBy(v => (symbology.dualband ? v : Math.abs(v)))
                .value();

            if (_.size(symbology.pushValueToRight) && _.isArray(symbology.pushValueToRight)) {
                _.forEach(symbology.pushValueToRight, v => {
                    range = _.filter(range, d => v !== d);
                });

                const scaleRange = d3
                    .scaleLinear()
                    .domain(range)
                    .range(theme);

                const fn = value => {
                    const swapValue = _.findIndex(symbology.pushValueToRight, v => v === value);
                    return scaleRange(swapValue > -1 ? scaleRange.domain()[scaleRange.domain().length - 1] : value);
                };
                fn.domain = () => {
                    const d = scaleRange.domain();
                    _.forEach(symbology.pushValueToRight, v => {
                        d.push(v);
                    });
                    return d;
                };
                return fn;
            }

            return d3
                .scaleLinear()
                .domain(range)
                .range(theme);
        }
    };

    return domainRange[symbology.scale || 'jenks']();
};

export const createSymbologyScale = (features, options) => {
    // console.time('L.VectorLayer.Core -> createSymbologyScale');
    const { symbology } = options;

    let scale = null;
    const domain = _(features)
        .map(feature => {
            const value = _.toNumber(feature.properties[options.value]);
            if (_.isNumber(value) && !_.isNaN(value)) {
                const _max = _.toNumber(symbology.max);
                const _min = _.toNumber(symbology.min);
                if (_.isNumber(_max) && value > _max) {
                    return _max;
                } else if (_.isNumber(_min) && value < _min) {
                    return _min;
                }
                return value;
            }
            return feature.properties[options.value];
        })
        .uniq()
        .sort()
        .value();

    if (!options.value || domain.length === 1) {
        if (options.value && symbology.theme && !_.isArray(symbology.theme)) {
            scale = val => getObjectThemeColor(symbology.theme, val, symbology.defaultColor);
            scale.domain = () => domain;
            return scale;
        }

        scale = () =>
            options.legend
                ? (symbology.theme || [])[0] || 'transparent'
                : (symbology.style || {}).fillColor || 'transparent';
        scale.domain = () => domain;
        return scale;
    }

    // return a function to set the color range based on data received
    scale = getDomainRange(domain, symbology);

    // console.timeEnd('L.VectorLayer.Core -> createSymbologyScale');
    return scale;
};

export const createBoundEnvelope = bounds => {
    return turf.envelope(
        turf.featureCollection([
            turf.point([bounds._northEast.lng, bounds._northEast.lat]),
            turf.point([bounds._southWest.lng, bounds._southWest.lat])
        ])
    );
};

export const getCurrentBounds = options => {
    return createBoundEnvelope(options.bounds);
};

export const filterFeature = (feature, options) => {
    // console.time("L.VectorLayerUtils -> filterFeature");
    let valid = true;
    const filter = options.filter || {};

    // filter by properties
    if (filter.properties) {
        valid = _.every(filter.properties, (val, key) => {
            if (_.isBoolean(val)) {
                return feature.properties[key] === val;
            }

            if (!propertyHasValue(feature.properties[key]) && !propertyHasValue(val) || (val.data && !val.data.length)) {
                return true;
            }

            if (_.isArray(val) || val.type) {
              return val.type === FilterTypeEnum.Include
                ? _.some(val.data, v => propertiesIncludes(v, feature.properties[key]))
                : _.some(val, v => propertiesEqual(v, feature.properties[key]));
            }

            return propertiesEqual(val, feature.properties[key]);
        });

        if (!valid) {
            return false;
        }
    }

    // filter by location
    if (filter.location) {
        try {
            valid = turf.booleanIntersects(feature, createBoundEnvelope(options.bounds));
        } catch (err) {
            console.log(err);
        }

        if (!valid) {
            return false;
        }
    }

    // filter if inside a polygon
    if (filter.shape) {
        try {
            if (_.isArray(filter.shape)) {
                if (turf.getType(feature) === 'Point') {
                    valid = _.some(filter.shape, s => turf.booleanPointInPolygon(feature, s));
                } else {
                    valid = _.some(filter.shape, s => turf.intersect(feature, s));
                }
            } else {
                if (turf.getType(feature) === 'Point') {
                    valid = turf.booleanPointInPolygon(feature, filter.shape);
                } else {
                    valid = turf.intersect(feature, filter.shape);
                }
            }
        } catch (err) {
            console.log(err);
        }

        if (!valid) {
            return false;
        }
    }

    // console.timeEnd("L.VectorLayerUtils -> filterFeature");
    return valid;
};

export const propertyHasValue = val => {
    return _.isArray(val) ? _.size(val) > 0 : _.isNumber(val) ? true : !!val;
};

export const propertiesEqual = (val1, val2) => {
    val1 = _.deburr(val1).toUpperCase();
    val2 = _.deburr(val2).toUpperCase();

    if (val1[0] === '!') {
        val1 = val1.substr(1);
        return val1 !== val2;
    }
    return val1 === val2;
};

export const propertiesIncludes = (val1, val2) => {
  if(_.isArray(val2)) {
    return val2.includes(val1);
  }

  return false;
};

export const filterFeatures = (features, options) => {
    return turf.featureCollection(_.filter(features, feature => filterFeature(feature, options)));
};

export const convertPolygonsToPoints = (featureCollection, options) => {
    const features = _(featureCollection.features)
        .filter(f => f && f.geometry)
        .map(f => {
            const feature = turf.centroid(f);
            feature.properties = { ...f.properties, [options.value]: f.properties[options.value] };
            return feature;
        })
        .value();
    return turf.featureCollection(features);
};



// WEBPACK FOOTER //
// ./src/scripts/layers/Leaflet/VectorLayer/VectorLayerUtils.js